import { useCallback, useRef } from "react";

/**
 * 계약 내용 같은 "스크롤 가능한 뷰포트"를 PC 에서 마우스 왼쪽 드래그로 손바닥(팬) 이동시키는 훅.
 * - 대상 컨테이너는 overflow:auto 여야 한다(내부가 넘쳐야 팬 의미가 있음).
 * - 서명/체크/주소/상세주소 같은 입력 컨트롤(button/input/textarea/select/a) 위에서 시작한 드래그는
 *   팬으로 가로채지 않는다(필드 조작 우선).
 * - 모바일 한 손가락 스크롤/핀치는 네이티브/기존 핸들러가 담당하므로 여기선 마우스만 다룬다.
 */
export function useDragPan() {
  const ref = useRef<HTMLDivElement | null>(null);
  const state = useRef({ x: 0, y: 0, left: 0, top: 0, active: false });

  const isInteractive = (target: EventTarget | null): boolean => {
    let node = target as HTMLElement | null;
    while (node && node !== ref.current) {
      const tag = node.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "BUTTON" ||
        tag === "SELECT" ||
        tag === "A"
      ) {
        return true;
      }
      node = node.parentElement;
    }
    return false;
  };

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return; // 왼쪽 버튼만
    if (isInteractive(e.target)) return; // 입력 컨트롤 위면 팬 안 함
    const el = ref.current;
    if (!el) return;
    // 스크롤할 게 없으면(내용이 뷰포트에 다 들어오면) 팬 불필요
    if (el.scrollWidth <= el.clientWidth && el.scrollHeight <= el.clientHeight) return;
    state.current = {
      x: e.clientX,
      y: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
      active: true,
    };
    el.style.cursor = "grabbing";
    el.style.userSelect = "none";
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    const s = state.current;
    const el = ref.current;
    if (!s.active || !el) return;
    el.scrollLeft = s.left - (e.clientX - s.x);
    el.scrollTop = s.top - (e.clientY - s.y);
  }, []);

  const end = useCallback(() => {
    const el = ref.current;
    if (el) {
      // 기본 커서는 grab(손바닥) 유지 — ""로 지우면 팬 한 번 후 손바닥 커서가 사라진다.
      el.style.cursor = "grab";
      el.style.userSelect = "";
    }
    state.current.active = false;
  }, []);

  return {
    ref,
    onMouseDown,
    onMouseMove,
    onMouseUp: end,
    onMouseLeave: end,
  };
}
